import type { Ref } from "vue";
import { get, set, createStore, del } from "idb-keyval";
import type { NuxtApp } from "#app";

export class NotificationMessage {
  public id: string;
  public timeout?: number;
  public timestamp: string;
  public hasDownload?: boolean;
  public seen?: boolean;

  constructor(
    public type: NotificationType,
    public message: string,
    public active: boolean,
    public persist: boolean = false,
    public showProgress: boolean = false,
    public progress: number = 0,
    public persistentType: PersistentType = PERSISTENT_TYPES.NONE,
    public number: number = 1,
  ) {
    this.id = $ck.utils.getUID();
    this.timestamp = new Date().toISOString();
  }
}

export type GenericMessageObject = { message: string };

export function isMessageObject(value: unknown): value is GenericMessageObject {
  return typeof value === "object" && !$ck.assert.isNull(value) && Object.hasOwn(value, "message");
}

export type ErrorNotificationOptions = MaybeRef<string | PossibleErrorResponses | GenericMessageObject>;

type StoredFile = {
  file: Blob;
  filename: string;
};

export interface NotificationHandle {
  messageObject: Ref<NotificationMessage>;
  close: () => void;
  hide: () => void;
  cancelTimeout: () => void;
  setTimeout: (val?: number) => void;
  persist: (val?: boolean, type?: PersistentType) => void;
  setFile: (file: Blob, filename: string) => Promise<void>;
  getFile: () => Promise<StoredFile | undefined>;
  setType: (val: NotificationType) => void;
  setMessage: (val: string) => void;
  setProgress: (val: number) => void;
  hideProgress: () => void;
  markSeen: () => void;
  incrementNumber: () => void;
}

export const useGlobalNotification = defineStore("GlobalNotification", () => {
  const notificationQueue = useLocalStorage<NotificationMessage[]>("KLEANZ_NOTIFICATIONS", [], {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => JSON.stringify(v.filter((el) => el.persist)),
    },
  });

  const fileStore = createStore("KLEANZ_DOWNLOADS", "id:blob");

  function __fallback<T extends keyof NotificationMessage>(
    key: T,
    fallback: NotificationMessage[T],
  ): NotificationMessage[T] {
    if (notificationQueue.value.length === 0) return fallback;
    return notificationQueue.value[0]?.[key] ?? fallback;
  }

  const currentActive = computed(() => notificationQueue.value.find((el) => el.active));

  const timeoutMap = new Map<string, ReturnType<typeof setTimeout>>();
  watch(
    currentActive,
    (val) => {
      if (val && val.timeout) {
        timeoutMap.set(
          val.id,
          setTimeout(() => {
            timeoutMap.delete(val.id);
            return val.persist ? hideMessage(val) : clearMessage(val);
          }, val.timeout),
        );
      }
    },
    { deep: true, immediate: true },
  );

  const message = computed(() => __fallback("message", ""));
  const type = computed(() => __fallback("type", $ck.utils.NotificationType.Success));
  const active = computed(() => __fallback("active", false));
  const showProgress = computed(() => __fallback("showProgress", false));
  const progress = computed(() => __fallback("progress", 0));

  function makeNotificationHandle(msg: Ref<NotificationMessage>): NotificationHandle {
    return {
      messageObject: msg,
      close() {
        clearMessage(msg.value);
      },
      hide() {
        hideMessage(msg.value);
      },
      cancelTimeout() {
        cancelTimeout(msg.value);
      },
      setTimeout(value?: number) {
        msg.value.timeout = value;
      },
      persist(value: boolean = true, type = PERSISTENT_TYPES.NONE) {
        msg.value.persist = value;
        msg.value.persistentType = type;
      },
      async setFile(file: Blob, filename: string) {
        msg.value.hasDownload = true;
        return set(msg.value.id, { file, filename }, fileStore);
      },
      async getFile() {
        return get(msg.value.id, fileStore);
      },
      setType(val: NotificationType) {
        msg.value.seen = false;
        msg.value.type = val;
      },
      setMessage(val: string) {
        msg.value.seen = false;
        msg.value.message = val;
      },
      setProgress(val: number) {
        msg.value.seen = false;
        msg.value.showProgress = true;
        msg.value.progress = val;
      },
      hideProgress() {
        msg.value.showProgress = false;
      },
      markSeen() {
        msg.value.seen = true;
      },
      incrementNumber() {
        msg.value.number += 1;
      },
    };
  }

  function getNotificationHandler(id: string) {
    const index = notificationQueue.value.findIndex((el) => el.id === id);
    if (index >= 0) {
      return makeNotificationHandle(toRef(notificationQueue.value, index));
    }
  }

  function __showMessage(
    updateType: NotificationType,
    updateMessage: string = "",
    updatePersist: boolean = false,
    updateShowProgress: boolean = false,
    updateProgress: number = 0,
  ): NotificationHandle {
    const message = new NotificationMessage(
      updateType,
      updateMessage,
      true,
      updatePersist,
      updateShowProgress,
      updateProgress,
    );
    const length = notificationQueue.value.push(message);
    return makeNotificationHandle(toRef(notificationQueue.value, length - 1));
  }

  function cancelTimeout(item?: NotificationMessage) {
    if (item) {
      item.timeout = undefined;
      if (timeoutMap.has(item.id)) {
        clearTimeout(timeoutMap.get(item.id));
        timeoutMap.delete(item.id);
      }
    }
  }

  function clearMessage(msg?: NotificationMessage) {
    if (msg) {
      const index = notificationQueue.value.findIndex((el) => el.id === msg.id);
      if (index >= 0) {
        const item = notificationQueue.value.splice(index, 1);
        cancelTimeout(item[0]);
        if (item[0]?.id) {
          del(item[0].id, fileStore).then((e) => devConsole.log("file removed.", e));
        }
      }
      return;
    }
    const item = notificationQueue.value.shift();
    if (item) {
      cancelTimeout(item);
      del(item.id, fileStore).then((e) => devConsole.log("file removed.", e));
    }
  }

  function clearAllPersist(active: boolean = false) {
    const persistent = notificationQueue.value.filter((el) => (el.persist && active ? true : !el.active));
    persistent.forEach((el) => {
      clearMessage(el);
    });
  }

  function clearAll() {
    notificationQueue.value = [];
  }

  function hideMessage(msg?: NotificationMessage) {
    let item;
    if (msg) {
      item = notificationQueue.value.find((el) => el.id === msg.id);
    } else {
      item = notificationQueue.value.find((el) => el.active);
    }
    if (item) item.active = false;
  }

  function checkForDuplicates(message: string) {
    let duplicate = false;
    notificationQueue.value.forEach((notification) => {
      if (notification.message === message) {
        const handler = getNotificationHandler(notification.id);
        handler?.incrementNumber();
        duplicate = true;
      }
    });
    return duplicate;
  }

  function getError(message: ErrorNotificationOptions = "") {
    const realMessage = toRaw(unref(message)); // Unwrapping in case it's a ref

    if (realMessage instanceof Error) {
      devConsole.error(realMessage);
      return realMessage.message;
    }
    if (isMessageObject(realMessage)) {
      return realMessage.message;
    }
    if (typeof realMessage === "string") {
      return realMessage;
    }
    return "";
  }

  function showError(message: ErrorNotificationOptions = "", persist: boolean = false) {
    const messageString = getError(message);
    if (checkForDuplicates(messageString)) return null;
    // Using this as a function and passing in the current nuxtApp context.
    const handle = __showMessage($ck.utils.NotificationType.Error, messageString, persist);
    handle.setTimeout($ck.utils.TimeInMS.TEN_SECONDS);
    return handle;
  }

  function showPending(
    message: string = "",
    showProgress: boolean = false,
    progress?: number,
    persist: boolean = true,
  ) {
    return __showMessage($ck.utils.NotificationType.Pending, message, persist, showProgress, progress);
  }

  function showSuccess(message: string = "", persist: boolean = false) {
    const handler = __showMessage($ck.utils.NotificationType.Success, message, persist);
    handler.setTimeout($ck.utils.TimeInMS.FIVE_SECONDS);
    return handler;
  }

  function showWarning(message: string = "", persist: boolean = false) {
    const handler = __showMessage($ck.utils.NotificationType.Warning, message, persist);
    handler.setTimeout($ck.utils.TimeInMS.FIVE_SECONDS);
    return handler;
  }

  function showDevError(...args: Parameters<typeof showError>) {
    if (import.meta.env.DEV) {
      const handle = showError(...args);
      if (handle) {
        handle.persist();
        handle.cancelTimeout();
      }
      return handle;
    }
  }

  return {
    type,
    message,
    active,
    progress,
    clearMessage,
    clearAllPersist,
    clearAll,
    hideMessage,
    showSuccess,
    showPending,
    showProgress,
    showError,
    showDevError,
    showWarning,
    notificationQueue: readonly(notificationQueue),
    currentActive,
    getNotificationHandler,
    getError,
  };
});

const PERSISTENT_HANDLERS: Record<
  PersistentType,
  (nuxtApp: NuxtApp, notification: NotificationMessage) => Promise<void>
> = {
  [PERSISTENT_TYPES.NONE]: async () => {},
};

// This needs to be called outside of the pinia store definition because the things inside it use the pinia store.
export const startPersistentNotificationInitializer = () => {
  const globalNotification = useGlobalNotification();
  const nuxtApp = useNuxtApp();

  globalNotification.notificationQueue.forEach((notification) => {
    if (
      notification.persist &&
      notification.persistentType &&
      $ck._.isFunction(PERSISTENT_HANDLERS[notification.persistentType])
    ) {
      PERSISTENT_HANDLERS[notification.persistentType]!(nuxtApp, notification);
    }
  });
};
